import { Button, Flex, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Dropdown, FormField } from "@/components/elements";
import { ConditionsTooltipContent } from "@/components/elements/TooltipContent";
import { getCountries } from "@/components/elements/UserOnBoarding/functions";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { ElementType, FC } from "react";

interface Props {
  WrapperForFields: ElementType;
  wrapperProps: Record<string, unknown>;
  form: any;
  showLessElements: boolean;
  activeStep: number;
  onCancel?: () => void;
  loading: boolean;
  nextStep: () => void;
}

const EditStep2: FC<Props> = ({
  WrapperForFields,
  wrapperProps,
  form,
  showLessElements,
  activeStep,
  onCancel,
  loading,
  nextStep,
}) => {
  const { t } = useTranslation();
  const countries = getCountries(t);

  const { data: plans } = usePlansUser();
  const isFreePlan = plans?.name === "starter";

  return (
    <Stack gap="md">
      <Title order={4}>{t("components.CreateCarForm.titles.vehicleDetails")}</Title>

      <WrapperForFields {...wrapperProps}>
        <TextInput
          label={t("car.licence_plate")}
          placeholder={t("components.CreateCarForm.placeholders.licence_plate")}
          {...form.getInputProps("licence_plate")}
        />

        <Dropdown
          label={t("car.country_of_registration")}
          placeholder={t("components.CreateCarForm.placeholders.country_of_registration")}
          data={countries}
          searchable
          value={form.values.country_of_registration}
          onChange={(value) => form.setFieldValue("country_of_registration", value || "")}
        />
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <FormField>
          <TextInput
            label={t("car.vin")}
            placeholder={t("components.CreateCarForm.placeholders.vin")}
            {...form.getInputProps("vin")}
          />
        </FormField>

        <FormField>
          <TextInput
            label={t("car.color")}
            placeholder={t("components.CreateCarForm.placeholders.color")}
            {...form.getInputProps("color")}
          />
        </FormField>
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <TextInput
          label={t("car.interior_colors")}
          placeholder={t("components.CreateCarForm.placeholders.interior_colors")}
          {...form.getInputProps("interior_colors")}
        />

        <Dropdown
          label={t("car.condition")}
          tooltip={<ConditionsTooltipContent />}
          disabled={isFreePlan}
          clearable
          showLessElements={showLessElements}
          data={[
            { value: "superb", label: t("car.conditions.superb") },
            { value: "excellent", label: t("car.conditions.excellent") },
            { value: "good", label: t("car.conditions.good") },
            { value: "fair", label: t("car.conditions.fair") },
          ]}
          value={form.values.condition}
          onChange={(value) => form.setFieldValue("condition", value)}
        />
      </WrapperForFields>

      <Group justify="space-between" mt="2em">
        <Text fw={500} fz="sm">
          {t("common.steps", { currentStep: activeStep + 1, totalSteps: 4 })}
        </Text>

        <Flex justify="end" gap="md">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              {t("common.cancel")}
            </Button>
          )}

          <Button type="button" onClick={nextStep}>
            {t("common.next")}
          </Button>
        </Flex>
      </Group>
    </Stack>
  );
};

export default EditStep2;
